import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Clock, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = 'last_activity_ts';

export default function IdleTimeoutManager() {
  const { user, logout } = useAuth();
  const [timeoutMin, setTimeoutMin] = useState(null);
  const [warningSec, setWarningSec] = useState(60);
  const [showWarning, setShowWarning] = useState(false);
  const [remaining, setRemaining] = useState(60);
  const warningRef = useRef(false);
  const loggingOut = useRef(false);

  // Zaman aşımı ayarını yükle
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    axios.get(`${API}/settings/session-config`).then(res => {
      if (!mounted) return;
      setTimeoutMin(res.data.idle_timeout_minutes || 60);
      setWarningSec(res.data.warning_seconds || 60);
    }).catch(() => { if (mounted) setTimeoutMin(60); });
    return () => { mounted = false; };
  }, [user]);

  const markActivity = useCallback(() => {
    if (warningRef.current) return; // uyarı açıkken yalnızca "Devam Et" oturumu uzatır
    localStorage.setItem(LS_KEY, String(Date.now()));
  }, []);

  // Aktivite dinleyicileri (1 sn throttle)
  useEffect(() => {
    if (!user || !timeoutMin) return;
    localStorage.setItem(LS_KEY, String(Date.now()));
    let last = 0;
    const handler = () => {
      const now = Date.now();
      if (now - last > 1000) { last = now; markActivity(); }
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, handler));
  }, [user, timeoutMin, markActivity]);

  const doLogout = useCallback(async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    try { await logout(); } catch (e) { /* yoksay */ }
    window.location.href = '/login?timeout=1';
  }, [logout]);

  // Periyodik kontrol (saniyede bir)
  useEffect(() => {
    if (!user || !timeoutMin) return;
    const iv = setInterval(() => {
      const lastAct = parseInt(localStorage.getItem(LS_KEY) || '0', 10);
      const idleMs = Date.now() - lastAct;
      const timeoutMs = timeoutMin * 60 * 1000;
      const warnMs = timeoutMs - warningSec * 1000;
      if (idleMs >= timeoutMs) {
        doLogout();
      } else if (idleMs >= warnMs) {
        warningRef.current = true;
        setShowWarning(true);
        setRemaining(Math.max(0, Math.ceil((timeoutMs - idleMs) / 1000)));
      } else if (warningRef.current) {
        // başka bir sekmede devam edilmiş
        warningRef.current = false;
        setShowWarning(false);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [user, timeoutMin, warningSec, doLogout]);

  const continueSession = async () => {
    warningRef.current = false;
    localStorage.setItem(LS_KEY, String(Date.now()));
    setShowWarning(false);
    try { await axios.get(`${API}/auth/me`); } catch (e) { /* 401 interceptor devralır */ }
  };

  if (!user || !showWarning) return null;

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent
        className="bg-gray-900 border-yellow-700/60 text-white max-w-md [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="idle-warning-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-yellow-400">
            <Clock className="w-5 h-5" /> Oturumunuz Kapanmak Üzere
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Uzun süredir işlem yapmadınız. Devam etmezseniz güvenlik nedeniyle oturumunuz otomatik kapatılacak.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 text-center">
          <div className="text-5xl font-bold text-yellow-400 tabular-nums" data-testid="idle-countdown">{remaining}</div>
          <p className="text-sm text-gray-400 mt-1">saniye kaldı</p>
        </div>
        <DialogFooter className="gap-2">
          <Button onClick={doLogout} variant="ghost" className="text-gray-400 hover:text-white" data-testid="idle-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
          </Button>
          <Button onClick={continueSession} className="bg-yellow-600 hover:bg-yellow-500 text-black font-semibold" data-testid="idle-continue-btn">
            Devam Et
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
